import "moment/locale/id";
import moment from "moment";
import { useState, useMemo } from "react";
import { parse as parseDate, isValid, format } from "date-fns";
import { id } from "date-fns/locale";

// Set moment to Indonesian
moment.locale("id");

export function useDateParsing() {
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    const formats = [
      "d MMMM yyyy",
      "dd MMMM yyyy",
      "d MMM yyyy",
      "dd MMM yyyy",
      "yyyy-MM-dd",
      "dd-MM-yyyy",
    ];

    for (let fmt of formats) {
      const date = parseDate(dateString, fmt, new Date(), { locale: id });
      if (isValid(date)) {
        return date;
      }
    }

    return null;
  };

  const formatDate = (dateString) => {
    const date = parseDateString(dateString);
    if (!date) return dateString;
    return format(date, "d MMM", { locale: id });
  };

  return { formatDate };
}

export function usePagination(data) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.ceil(data.length / itemsPerPage);

  const startIndex = data.length - currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const safeStartIndex = Math.max(0, startIndex);
  const currentItems = data.slice(safeStartIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return {
    totalPages,
    currentItems,
    handlePageChange,
    currentPage,
  };
}

export function useNavlinks() {
  const navLinks = useMemo(
    () => [
      { link: "/", name: "Beranda" },
      {
        link: "",
        name: "Profil",
        subMenu: [
          { name: "Visi & Misi", link: "/profile/visi-misi" },
          { name: "Struktur Organisasi", link: "/profile/struktur-organisasi" },
          { name: "Sejarah", link: "/profile/sejarah" },
          { name: "Alumni", link: "/profile/alumni" },
          { name: "Tupoksi", link: "/profile/tupoksi" },
          { name: "Legalitas", link: "/profile/legalitas" },
        ],
      },
      {
        link: "",
        name: "Publikasi",
        subMenu: [
          { name: "Prestasi", link: "/publikasi/prestasi" },
          { name: "Berita", link: "/publikasi/berita" },
          { name: "Karya Siswa", link: "/publikasi/karya-siswa" },
          { name: "Komunitas", link: "/publikasi/komunitas" },
          { name: "Agenda", link: "/publikasi/agenda" },
          { name: "Galeri", link: "/publikasi/galeri" },
        ],
      },
      { link: "https://linktr.ee/pesantrenidrisiyyah", name: "SNMB / PMB" },
      {
        link: "",
        name: "Bidang",
        subMenu: [
          { name: "Akademik", link: "/bidang/akademik" },
          { name: "Sarana & Prasarana", link: "/bidang/sarana-prasarana" },
          { name: "Kesiswaan", link: "/bidang/kesiswaan" },
          { name: "Keasramaan", link: "/bidang/keasramaan" },
          { name: "Humas", link: "/bidang/humas" },
          { name: "Program Unggulan", link: "/bidang/program-unggulan" },
          { name: "Tata Usaha", link: "/bidang/tata-usaha" },
        ],
      },
      {
        link: "",
        name: "Layanan",
        subMenu: [
          { name: "Edutren", link: "https://www.edutren.id/" },
          {
            name: "Perpustakaan Digital",
            link: "/layanan/perpustakaan-digital",
          },
          { name: "E-Report", link: "https://raportma.idrisiyyah.sch.id/" },
        ],
      },
    ],
    [],
  );

  return { navLinks };
}

export function useDayName() {
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    const formats = [
      "DD MMMM YYYY",
      "D MMMM YYYY",
      "DD MMM YYYY",
      "D MMM YYYY",
      "YYYY-MM-DD",
      "DD-MM-YYYY",
    ];

    const m = moment(dateString, formats, true);
    if (m.isValid()) return m.toDate();
    return null;
  };

  const getDayName = (dateString) => {
    const date = parseDateString(dateString);
    if (!date) return "";
    return moment(date).format("dddd");
  };

  const getTranslatedDayName = (dateString) => {
    return getDayName(dateString);
  };

  const formatDate = (dateString, formatStr = "d MMM") => {
    const date = parseDateString(dateString);
    if (!date) return dateString;
    return format(date, formatStr, { locale: id });
  };

  return {
    formatDate,
    getDayName,
    getTranslatedDayName,
  };
}

export function useConvertToSlug(title) {
  const convertToSlug = (judul) => {
    if (!judul) return "";
    return judul
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  const slug = useMemo(() => convertToSlug(title), [title]);
  return slug;
}

export function handleUpdateDate(handleChange) {
  const UpdateDate = (date) => {
    const formattedDate = moment(date).format("DD MMMM YYYY");
    handleChange({
      target: {
        name: "tanggal",
        value: formattedDate,
      },
    });
  };

  return { UpdateDate };
}

export function handleAddDate(setData) {
  const AddDate = (date) => {
    const formattedDate = moment(date).format("DD MMMM YYYY");
    setData((prevData) => ({
      ...prevData,
      tanggal: formattedDate,
    }));
  };

  return { AddDate };
}

// Helper function - taruh di luar komponen atau di file utils
export function resolveGambar(gambar) {
  if (!gambar) return "";

  // Jika sudah array asli
  if (Array.isArray(gambar)) return gambar[0] || "";

  // Jika string, coba parse sebagai JSON dulu
  if (typeof gambar === "string") {
    try {
      const parsed = JSON.parse(gambar);
      if (Array.isArray(parsed)) return parsed[0] || "";
      return parsed; // kalau ternyata JSON string biasa
    } catch {
      // bukan JSON valid, anggap string biasa (nama file langsung)
      return gambar;
    }
  }

  return "";
}
